/**
 * One codebase, two shapes.
 *
 * Mobile-first with the web build carrying desktop. The split is by **width**, never by platform: a
 * phone-sized window on a desktop should behave like a phone, and a tablet gets the wide layout.
 * Platform is only for platform mechanics — safe areas, the status bar.
 *
 * **This is the only file that chooses between the side rail and the bottom bar.** Screens
 * rearrange their own pieces from one body of state; nothing below here decides which shell it is
 * in, and no component reads the window width at all.
 */
import '@/../global.css';

import { BottomNav, NarrowHeader, SideRail } from '@/components/nav';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { api } from '@/api';
import { cn } from '@/lib/utils';
import { consumePairingLink, watchPairingLinks } from '@/lib/pairing';
import { BacktestScreen } from '@/screens/Backtest';
import { BetsScreen } from '@/screens/Bets';
import { CompleteScreen } from '@/screens/Complete';
import { LeaguesScreen } from '@/screens/Leagues';
import { PredictionsScreen } from '@/screens/Predictions';
import { SettingsScreen } from '@/screens/Settings';
import { UpcomingScreen } from '@/screens/Upcoming';
import { preload } from '@/storage';
import { themeWasChosen, useStore, type Screen } from '@/store';
import { useLayout } from '@/useLayout';
import { colorScheme } from 'nativewind';
import * as React from 'react';
import { Platform, StatusBar, useColorScheme, View } from 'react-native';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';

export default function App() {
  const systemTheme = useColorScheme();
  const { theme, setTheme, setPaired, load } = useStore();
  const [ready, setReady] = React.useState(false);

  // Storage is async on native, so the key and the mode are read once before the first real paint.
  // Without this a paired device flashes the pairing screen on every cold start.
  React.useEffect(() => {
    void (async () => {
      await preload(['psb.theme', 'psb.key', 'psb.api']);
      await api.load();
      // After the stored key is read, so a link deliberately overrides what is already there —
      // pairing again is how a device moves to a rotated key.
      await consumePairingLink();
      setPaired(api.paired);
      setReady(true);
      if (api.paired) void load();
    })();
  }, [setPaired, load]);

  // A link can also arrive while the app is already open, which on native is the usual case.
  React.useEffect(
    () =>
      watchPairingLinks(() => {
        setPaired(api.paired);
        void load();
      }),
    [setPaired, load],
  );

  // The store owns the mode so the toggle works, but the system preference decides the first paint
  // on a device that has never chosen one.
  React.useEffect(() => {
    if (systemTheme && !themeWasChosen()) setTheme(systemTheme);
  }, [systemTheme, setTheme]);

  // NativeWind is told, not asked: the store stays the single owner and this hands its answer to
  // the styling layer, which flips the `dark` class and with it every variable in global.css.
  React.useEffect(() => {
    colorScheme.set(theme);
  }, [theme]);

  return (
    <SafeAreaProvider>
      <Shell ready={ready} />
    </SafeAreaProvider>
  );
}

function Shell({ ready }: { ready: boolean }) {
  const { wide } = useLayout();
  const { screen, go, error, dismissError, theme, toggleTheme, paired, matches, bets } = useStore();
  // A plain View with inset padding, not RN's SafeAreaView: NativeWind does not style SafeAreaView
  // on native, which drops both the themed background and the root flex-1 the whole layout hangs on.
  const insets = useSafeAreaInsets();

  const badge = {
    predictions: matches.filter((m) => m.prediction).length,
    bets: bets.length,
    live: bets.filter((b) => {
      const t = Date.parse(b.kickoffUtc);
      return Number.isFinite(t) && Date.now() >= t && Date.now() <= t + 3 * 3600_000;
    }).length,
  };

  return (
    <View
      className="bg-background flex-1"
      style={{
        paddingTop: insets.top,
        paddingBottom: insets.bottom,
        paddingLeft: insets.left,
        paddingRight: insets.right,
      }}>
      <StatusBar barStyle={theme === 'dark' ? 'light-content' : 'dark-content'} />

      <View className={cn('flex-1', wide ? 'flex-row' : 'flex-col')}>
        {wide ? (
          <SideRail
            screen={screen}
            onGo={go}
            theme={theme}
            onToggleTheme={toggleTheme}
            badge={badge}
          />
        ) : null}

        <View className={cn('min-w-0 flex-1 gap-3', wide ? 'p-6' : 'p-4')}>
          {wide ? null : <NarrowHeader theme={theme} onToggleTheme={toggleTheme} />}

          {error ? (
            <Alert variant="destructive" icon="▲">
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
              <Button variant="ghost" size="sm" className="self-start" onPress={dismissError}>
                <Text>Dismiss</Text>
              </Button>
            </Alert>
          ) : null}

          <View className="flex-1">
            {!ready ? null : paired || screen === 'settings' ? (
              <CurrentScreen screen={screen} />
            ) : (
              <SettingsScreen />
            )}
          </View>
        </View>

        {wide ? null : <BottomNav screen={screen} onGo={go} />}
      </View>
    </View>
  );
}

function CurrentScreen({ screen }: { screen: Screen }) {
  switch (screen) {
    case 'upcoming':
      return <UpcomingScreen />;
    case 'predictions':
      return <PredictionsScreen />;
    case 'bets':
      return <BetsScreen />;
    case 'live':
      return <BetsScreen live />;
    case 'complete':
      return <CompleteScreen />;
    case 'leagues':
      return <LeaguesScreen />;
    case 'backtest':
      return <BacktestScreen />;
    case 'settings':
      return <SettingsScreen />;
    // Every screen has a case above; this keeps an unknown one from crashing the shell rather than
    // standing in for a tab that has not been built.
    default:
      return null;
  }
}
